import { addMonths, isSameDay, isSameMonth, startOfMonth } from 'date-fns/esm'

import { formatLocale } from '@/modules/shared/helpers/locale'

import type { TimelineActivity, TimelineDay } from '../Timeline'

type TimelineSection = {
  id: string
  date: Date
  days: TimelineDay[]
}

function groupTimelineActivitiesByDay(activities: TimelineActivity[]) {
  const timelineActivitiesByDay = activities.reduce<TimelineDay[]>((accumulator, current) => {
    const date = new Date(current.start)
    const id = formatLocale(date, 'dd-MM-yyyy')

    const existingDay = accumulator.find((day) => isSameDay(day.date, date))
    return existingDay
      ? accumulator.map((day) =>
          day.id === id
            ? {
                ...day,
                activities: [...day.activities, current],
              }
            : day,
        )
      : [...accumulator, { id, date, activities: [current] }]
  }, [])

  // Add today by default if it is not in the array yet
  const today = new Date()
  const todayId = formatLocale(today, 'dd-MM-yyyy')
  const doesTodayExist = timelineActivitiesByDay.find((day) => day.id === todayId)
  if (!doesTodayExist) {
    timelineActivitiesByDay.push({
      id: todayId,
      date: today,
      activities: [],
    })
  }

  // Sort TimelineDays by date
  return timelineActivitiesByDay.sort((a, b) => a.date.getTime() - b.date.getTime())
}

function groupTimelineActivitiesByMonth(activities: TimelineActivity[]) {
  const timelineActivitiesByDay = groupTimelineActivitiesByDay(activities)

  const groupedActivitiesByMonth = timelineActivitiesByDay.reduce<TimelineSection[]>((accumulator, current) => {
    const id = formatLocale(current.date, 'MM-yyyy')
    const date = startOfMonth(current.date)
    const existingSection = accumulator.find((section) => isSameMonth(section.date, current.date))
    return existingSection
      ? accumulator.map((section) =>
          section.id === id
            ? {
                ...section,
                days: [...section.days, current],
              }
            : section,
        )
      : [...accumulator, { id, date, days: [current] }]
  }, [])

  // Make sure that every month has a section entry
  const earliestActivityDate = timelineActivitiesByDay.at(0)?.date
  const latestActivityDate = timelineActivitiesByDay.at(-1)?.date

  if (earliestActivityDate && latestActivityDate) {
    let iterationDate = addMonths(startOfMonth(earliestActivityDate), 1)
    while (iterationDate.getTime() < startOfMonth(latestActivityDate).getTime()) {
      const id = formatLocale(iterationDate, 'MM-yyyy')
      const doesMonthExist = groupedActivitiesByMonth.find((month) => month.id === id)
      if (!doesMonthExist) {
        groupedActivitiesByMonth.push({
          id,
          date: iterationDate,
          days: [],
        })
      }

      iterationDate = addMonths(iterationDate, 1)
    }
  }

  // Sort TimelineMonths by months
  return groupedActivitiesByMonth.sort((a, b) => a.date.getTime() - b.date.getTime())
}

export { groupTimelineActivitiesByDay, groupTimelineActivitiesByMonth }
